import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import EnvironmentUtils from '@spothero/utils/environment';
import {SEARCH_TYPE} from '../search-prop-types';
import WithConfig from 'config/WithConfig';
import Alert from '@spothero/ui/alert';
import Button from '@spothero/ui/button';
import TextButton from '@spothero/ui/text-button';
import SHLoader from 'common/loader/SHLoader';
import SpotListItem from './SpotListItem';
import SpotListItemRow from './SpotListItemRow';
import SpotListItemRowPlaceholder from './SpotListItemRowPlaceholder';
import {updateSelected} from 'spot/spot-actions';
import {cancelSpotRequests} from 'spots/spots-actions';
import {resetFilters} from 'filters/filters-actions';

const isMobile = EnvironmentUtils.isMobileDevice();

const SpotList = ({
    searchType,
    spots,
    numActiveFilters,
    lowPrice,
    highPrice,
    isMonthly,
    starts,
    ends,
    event,
    siteUrl,
    destination,
    city,
    urlDateFormat,
    showListLoader,
    showLoadingComplete,
    loadNextPage,
    onResetFiltersClick,
    updateSelectedSpot,
    cancelAllSpotRequests,
    mapLoadingIncrement,
    isPrerender,
    isRebookingReservation,
    isAdmin
}) => {
    const end = (isMonthly)
        ? moment(starts, moment.ISO_8601)
            .add(1, 'month')
            .format(urlDateFormat)
        : ends;
    const ListItemComponent = (isMobile)
        ? SpotListItemRow
        : SpotListItem;
    const url = (searchType === 'event' && !isEmpty(event))
        ? `${siteUrl}/${event.search_url}` // eslint-disable-line camelcase
        : (destination)
            ? `${siteUrl}/${city.slug}/${(isMonthly && destination.monthly_relative_url) ? destination.monthly_relative_url : destination.relative_url}?starts=${starts}&ends=${ends}`
            : `${siteUrl}/${(isMonthly && city.monthly_relative_url) ? city.monthly_relative_url : city.relative_url}?starts=${starts}&ends=${ends}`;

    return (
        <div
            className="SpotList"
            itemProp="offers"
            itemScope
            itemType="http://schema.org/AggregateOffer"
        >
            {url &&
                <meta
                    itemProp="url"
                    content={url}
                />
            }
            {(!isMobile && ((mapLoadingIncrement > 0 && spots.length < 1) || isPrerender)) &&
                <SHLoader />
            }
            {spots.length > 0 &&
                <span>
                    {lowPrice &&
                        <meta
                            itemProp="lowPrice"
                            content={`$${lowPrice}`}
                        />
                    }
                    {highPrice &&
                        <meta
                            itemProp="highPrice"
                            content={`$${highPrice}`}
                        />
                    }
                    <meta
                        itemProp="validFrom"
                        content={starts}
                    />
                    <meta
                        itemProp="validThrough"
                        content={end}
                    />
                </span>
            }
            <meta
                itemProp="offerCount"
                content={spots.length}
            />
            <div className="SpotList-spots">
                {spots
                    .filter(({selectedRate}) => {
                        // ensure spots with either missing selectedRate
                        // or those without the proper selectedRate.rule_type are not offered to users

                        return selectedRate && (
                            (isMonthly && selectedRate.rule_type === 'monthly')
                            || (!isMonthly && selectedRate.rule_type !== 'monthly')
                        );
                    })
                    .map(spot => {
                        return (
                            <ListItemComponent
                                searchType={searchType}
                                key={spot.spotId}
                                spot={spot}
                                isMonthly={isMonthly}
                                isRebookingReservation={isRebookingReservation}
                                isAdmin={isAdmin}
                                updateSelectedSpot={updateSelectedSpot}
                                cancelAllSpotRequests={cancelAllSpotRequests}
                            />
                        );
                    })
                }
            </div>
            {(isMobile && !showLoadingComplete && !showListLoader && (!spots || spots.length === 0)) &&
                <div className="SpotList-spots">
                    <SpotListItemRowPlaceholder />
                    <SpotListItemRowPlaceholder />
                    <SpotListItemRowPlaceholder />
                    <SpotListItemRowPlaceholder />
                </div>
            }
            {(isMobile && showListLoader) &&
                <SHLoader />
            }
            {(isMobile && showLoadingComplete) &&
                <div className="SpotList-actions">
                    <Alert
                        className="text-center"
                        type="muted"
                    >
                        <strong>Still haven&apos;t found a spot?</strong> Try searching a different destination or time above.
                        {(numActiveFilters > 0) &&
                            <span>
                                {' '}Or
                                <TextButton
                                    onClick={onResetFiltersClick}
                                >
                                    reset filters
                                </TextButton>
                                .
                            </span>
                        }
                    </Alert>
                </div>
            }
            {(isMobile && !showListLoader && !showLoadingComplete) &&
                <div className="SpotList-actions">
                    <Button
                        color="secondary"
                        onClick={loadNextPage}
                    >
                        Load More Spots
                    </Button>
                </div>
            }
        </div>
    );
};

SpotList.propTypes = {
    numActiveFilters: PropTypes.number,
    lowPrice: PropTypes.number,
    highPrice: PropTypes.number,
    isMonthly: PropTypes.bool.isRequired,
    showListLoader: PropTypes.bool,
    showLoadingComplete: PropTypes.bool,
    searchType: SEARCH_TYPE.isRequired,
    starts: PropTypes.string,
    ends: PropTypes.string,
    spots: PropTypes.arrayOf(PropTypes.object),
    siteUrl: PropTypes.string,
    urlDateFormat: PropTypes.string,
    loadNextPage: PropTypes.func,
    onResetFiltersClick: PropTypes.func,
    cancelAllSpotRequests: PropTypes.func.isRequired,
    updateSelectedSpot: PropTypes.func.isRequired,
    mapLoadingIncrement: PropTypes.number,
    isPrerender: PropTypes.bool,
    isRebookingReservation: PropTypes.bool,
    isAdmin: PropTypes.bool,
    event: PropTypes.object,
    destination: PropTypes.object,
    city: PropTypes.object
};

SpotList.defaultProps = {
    showListLoader: false,
    showLoadingComplete: false
};

const mapStateToProps = state => {
    const {
        search: {
            data: {
                currentIncrement: mapLoadingIncrement,
                searchType
            }
        },
        searchRequest: {
            starts,
            ends,
            monthly: isMonthly
        },
        checkout: {
            isRebookingReservation
        },
        filters: {
            numActive: numActiveFilters,
            priceLow: lowPrice,
            priceHigh: highPrice,
        },
        user: {
            data: {
                isAdmin
            }
        },
        city: {
            data: city
        },
        destination: {
            data: destination
        },
        event: {
            data: event
        }
    } = state;

    return {
        searchType,
        isMonthly,
        starts,
        ends,
        numActiveFilters,
        lowPrice,
        highPrice,
        event,
        destination,
        city,
        mapLoadingIncrement,
        isRebookingReservation,
        isAdmin
    };
};

const mapDispatchToProps = {
    onResetFiltersClick: resetFilters,
    cancelAllSpotRequests: cancelSpotRequests,
    updateSelectedSpot: updateSelected
};

const enhance = compose(
    WithConfig,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(SpotList);
